import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { filter } from "rxjs/operators";
import { SubSink } from "subsink";
import { MessagingService } from "../../../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../../../core/messaging/severity-type.enum";
import { GridPipeName } from "../../../../../../shared/grid/grid-pipe.enum";
import { GridColumnDefinition } from "../../../../../../shared/grid/models/grid-column-definition.model";
import { GridConfiguration } from "../../../../../../shared/grid/models/grid-configuration.model";
import { GridRequest } from "../../../../../../shared/grid/models/grid-request.model";
import { DateHelper } from "../../../../../../utilities/contracts/date-helper";
import { DateFormats } from "../../../../../../utilities/contracts/helper-types";
import { NumberHelper } from "../../../../../../utilities/contracts/number-helper";
import { AuditPageEntryService } from "../../../../member/chase-detail/chase-detail-chart/audit-page-entry/audit-page-entry.service";
import { ProjectType } from "../../../../project/project-type.enum";
import { AuditPackageContext } from "../../../models/audit-package-context.model";
import { AuditDataSharingService } from "../../../services/audit-data-sharing.service";
import { AuditPackageService } from "../../../services/audit-package.service";
import { AuditQueryGridFilters } from "../../audit-query-shared/audit-query-grid-filters/audit-query-grid-filter-model";
import { IAuditQueryGridData } from "../../audit-query-shared/audit-query-grid-filters/member-radv-record.model";
import { AuditQueryGridRiskService } from "./audit-query-grid-risk.service";

@Component({
  selector: "app-audit-query-grid-risk",
  templateUrl: "./audit-query-grid-risk.component.html",
  styleUrls: ["./audit-query-grid-risk.component.scss"],
})
export class AuditQueryGridRiskComponent implements OnInit, OnDestroy {
  private sink = new SubSink();
  @Input() stateName = "";
  gridfilters: AuditQueryGridFilters;
  gridData: IAuditQueryGridData[] = [];
  isGridVisible = false;
  ProjectType = ProjectType;
  memberRadvCardData: IAuditQueryGridData[] = [];

  auditGridConfiguration = new GridConfiguration();
  auditGridSearchRequest: GridRequest = new GridRequest();
  auditGridselection: any[] = [];
  refreshGrid = new EventEmitter<GridConfiguration>(true);

  private auditPackages: any[] = [];
  private pageEntriesMap: Map<number, any[]> = new Map();
  isAuditPackageLocked = false; // To disable grid actions.
  totalAuditPackagesCount = 0;
  @ViewChild("actionItem", { static: true }) actionItem: TemplateRef<any>;
  @ViewChild("auditReadyItem", { static: true }) auditReadyItem: TemplateRef<any>;
  @ViewChild("userRankItem", { static: true }) userRankItem: TemplateRef<any>;

  constructor(
    private auditQueryGridRiskService: AuditQueryGridRiskService,
    private changeDetector: ChangeDetectorRef,
    private auditPageEntryService: AuditPageEntryService,
    private auditPackageService: AuditPackageService,
    private auditDataSharingService: AuditDataSharingService,
    private messagingService: MessagingService
  ) { }

  ngOnInit() {
    this.createGrid();
    this.sink.add(
      this.auditQueryGridRiskService.auditGridFilter
        .subscribe(data => {
          this.gridfilters = data;
          if (this.gridfilters?.enrollee) {
            this.loadAuditPackagesCount(this.gridfilters.enrollee);
          }
          if (this.shouldShowGrid()) {
            this.populateGridWithData();
            this.isGridVisible = true;
            setTimeout(() => {
              this.refreshGrid.emit();
            },         100);
          } else {
            this.isGridVisible = false;
            this.changeDetector.markForCheck();
          }
        }),

      this.auditDataSharingService
        .getAuditPackagesObservable()
        .pipe(filter(p => p.length > 0))
        .subscribe(packages => {
          this.updateGridDataWithLockStatus(packages[0].context);
        }),

      this.auditQueryGridRiskService.gridRefreshFromStateFilter
        .subscribe(updatedData => {
          if (this.shouldShowGrid()) {
            if (updatedData && updatedData.icdCode) {
              this.updateSpecificRowInGrid(updatedData);
            } else {
              const updatedEnrollee = updatedData && updatedData.enrollee ? updatedData.enrollee : undefined;
              this.populateGridWithData(updatedEnrollee);
            }
          }
        }),

      this.auditQueryGridRiskService.memberRadvCard
        .subscribe(memberRadvCardData => {
          this.memberRadvCardData = memberRadvCardData;
        })

    );
  }

  ngOnDestroy() {
    this.sink.unsubscribe();
  }

  getEnrolleeFullName(): string {
    if (!this.gridfilters) {
      return "";
    }
    const firstName = this.gridfilters.enrolleeFirstName || "";
    const lastName = this.gridfilters.enrolleeLastName || "";
    return `${firstName} ${lastName}`.trim();
  }

  getEnrolleeDOB(): string {
    if (!this.gridfilters || !this.gridfilters.enrolleeDateOfBirth) {
      return "";
    }
    return DateHelper.format(this.gridfilters.enrolleeDateOfBirth, DateFormats.DEFAULT);
  }


  private updateSpecificRowInGrid(updatedData: any): void {
    this.gridData = this.gridData.map(row => {
      const isTargetRow = row.icdCode === updatedData.icdCode; // using icdCode as ChaseId same for all radv rows.
      if (isTargetRow) {
        return {
          ...row,
          auditReady: updatedData.auditReady !== undefined ? updatedData.auditReady : row.auditReady,
          userRank: updatedData.userRank !== undefined ? updatedData.userRank : row.userRank,
          auditPackageItemId: updatedData.auditPackageItemId !== undefined ? updatedData.auditPackageItemId.toString() : row.auditPackageItemId,
          auditReadyUpdateUser: updatedData.auditReadyUpdateUser !== undefined ? updatedData.auditReadyUpdateUser : row.auditReadyUpdateUser,
        };
      }
      return row;
    });

    this.changeDetector.markForCheck();
    this.refreshGrid.emit();
  }

  private shouldShowGrid(): boolean {
    return !!(this.gridfilters?.projectTypeId === ProjectType.RISK && this.gridfilters?.projectId && this.gridfilters?.enrollee);
  }


  private createGrid(): void {
    this.auditGridConfiguration = new GridConfiguration({
      columns: [
        new GridColumnDefinition({ field: "chaseId", header: "Chase ID", routeUrl: "/members/chase/:chaseId", target: "_blank" }),
        new GridColumnDefinition({ field: "source", header: "Source" }),
        new GridColumnDefinition({ field: "dateOfService", header: "Date of Service", pipeName: GridPipeName.Date, format: DateFormats.GRID_DATE_FORMAT }),
        new GridColumnDefinition({ field: "icdCode", header: "ICD Code" }),
        new GridColumnDefinition({ field: "hccCode", header: "HCC" }),
        new GridColumnDefinition({ field: "cmsModel", header: "CMS Model" }),
        new GridColumnDefinition({ field: "validationResultCode", header: "VRC Code" }),
        new GridColumnDefinition({ field: "chartPageNumber", header: "MR Page #" }),
        new GridColumnDefinition({ field: "initialRank", header: "Reveleer Initial Ranking" }),
        new GridColumnDefinition({ field: "finalRank", header: "Reveleer Final Ranking" }),
        new GridColumnDefinition({ field: "userRank", header: "Audit Package User Ranking", template: this.userRankItem, isSortableColumn: true }),
        new GridColumnDefinition({ field: "auditReady", header: "Audit Ready", template: this.auditReadyItem, isSortableColumn: true }),
        new GridColumnDefinition({ field: "auditReadyUpdateUser", header: "User" }),
        new GridColumnDefinition({ header: "Actions", template: this.actionItem, isSortableColumn: false }),
      ],
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100],
      selectionMode: "none",
    });

    this.auditGridConfiguration.showExportAction = false;
    this.auditGridConfiguration.showActionColumn = false;
  }

  private populateGridWithData(updatedEnrollee?: number): void {
    const enrollee = updatedEnrollee || this.gridfilters?.enrollee;
    if (enrollee) {
      this.gridData = this.transformRadvDataToGridData(this.memberRadvCardData);
      this.loadUserRanksFromPageEntries(enrollee);
      this.changeDetector.markForCheck();
      this.refreshGrid.emit();
    } else {
      this.gridData = [];
    }
  }

  private transformRadvDataToGridData(radvCardData: IAuditQueryGridData[]): IAuditQueryGridData[] {
    const gridData: IAuditQueryGridData[] = [];

    radvCardData.forEach(record => {
      if (this.shouldIncludeDiagnosis(record)) {
        gridData.push(record);
      }
    });

    return gridData;
  }

  private shouldIncludeDiagnosis(diagnosisData: IAuditQueryGridData): boolean {
    if (this.gridfilters?.icdCode) {
      const selectedIcdCodes = this.gridfilters.icdCode.split(",").map(code => code.trim());

      const hasMatchingIcd = selectedIcdCodes.some(selectedCode => {
        if (!diagnosisData.icdCode) {
          return false;
        }
        return selectedIcdCodes.includes(diagnosisData.icdCode);
      });

      if (!hasMatchingIcd) {
        return false;
      }
    }

    if (this.gridfilters?.vrcCode) {
      if (!diagnosisData.validationResultCode) {
        return false;
      }
      const selectedVrcCodes = this.gridfilters.vrcCode.split(",").map(code => code.trim());
      const recordVrcCode = diagnosisData.validationResultCode.trim();
      const hasMatchingVrc = selectedVrcCodes.some(selectedCode => {
        return recordVrcCode?.split(",").map(code => code.trim()).includes(selectedCode);
      });
      if (!hasMatchingVrc) {
        return false;
      }
    }

    if (this.gridfilters?.hccCode) {
      const selectedHccCodes = this.gridfilters.hccCode.split(",").map(code => code.trim());
      const hasMatchingHcc = selectedHccCodes.some(selectedCode => {
            return diagnosisData.hccCode?.toString()?.split(",").map(code => code.trim()).includes(selectedCode);
          });
      if (!hasMatchingHcc) {
        return false;
      }
    }

    return true;
  }


  private loadUserRanksFromPageEntries(updatedEnrollee?: number): void {
    const enrollee = updatedEnrollee || this.gridfilters?.enrollee;
    if (!enrollee || !this.gridfilters?.projectId) {
      return;
    }

    if (updatedEnrollee && this.gridfilters) {
      this.gridfilters.enrollee = updatedEnrollee;
    }

    this.auditDataSharingService.clearAllData();

    this.auditPackageService.getActiveAuditPackageForMember(enrollee)
      .subscribe({
        next: auditPackages => {
          const validAuditPackages = (auditPackages || []).filter(pkg => pkg.memberID === enrollee);

          this.auditPackages = validAuditPackages;
          this.auditDataSharingService.setAuditPackages(validAuditPackages);

          if (validAuditPackages.length > 0) {
            validAuditPackages.forEach(auditPackage => {
              if (auditPackage.auditPackageID) {
                this.loadPageEntriesForRisk(auditPackage.auditPackageID);
              }
            });
          } else {
            this.auditDataSharingService.clearAllData();
          }
        },
      });
  }

  private loadPageEntriesForRisk(auditPackageId: number): void {
    this.auditPageEntryService.getPageEntriesForRisk(auditPackageId)
      .subscribe({
        next: response => {
          const pageEntriesList = Array.isArray(response) ? response : [response];

          const validPageEntries = pageEntriesList.filter(pageEntries => {
            if (pageEntries && pageEntries.riskPageData) {
              return pageEntries.pageEntries && pageEntries.pageEntries.length > 0;
            }
            return true;
          });

          this.pageEntriesMap.set(auditPackageId, validPageEntries);
          this.auditDataSharingService.setPageEntries(auditPackageId, validPageEntries);

          pageEntriesList.forEach(pageEntries => {
            if (pageEntries) {
              const auditPackageItemId = pageEntries.auditPackageItemId;
              if (pageEntries.riskPageData) {
                const entityId = pageEntries.riskPageData.entityId;
                const userRank = pageEntries.riskPageData.userRank;
                const chaseId = pageEntries.riskPageData.chaseId || pageEntries.chaseId;
                this.updateGridDataWithUserRank(chaseId, entityId, userRank, auditPackageItemId);
              } else if (auditPackageItemId) {
                this.updateGridDataWithAuditPackageItemId(pageEntries.chaseId, auditPackageItemId);
              }
            }
          });
        },
        error: error => {
        },
      });
  }

  private updateGridDataWithUserRank(chaseId: number, entityId: number, userRank: number, auditPackageItemId?: number): void {
    this.gridData = this.gridData.map(item => {
      const chaseIdMatch = item.chaseId === chaseId;
      const entityIdMatch = item.entityId === entityId ||
                           item.entityId === Number(entityId) ||
                           Number(item.entityId) === entityId ||
                           String(item.entityId) === String(entityId);

      if (chaseIdMatch && entityIdMatch) {
        return {
          ...item,
          userRank,
          auditPackageItemId: auditPackageItemId ? auditPackageItemId.toString() : item.auditPackageItemId,
        };
      }
      return item;
    });

    setTimeout(() => {
      this.refreshGrid.emit();
      this.changeDetector.markForCheck();
    },         100);
  }

  private updateGridDataWithAuditPackageItemId(chaseId: number, auditPackageItemId: number): void {
    this.gridData = this.gridData.map(item => {
      if (item.chaseId === chaseId) {
        return {
          ...item,
          auditPackageItemId: auditPackageItemId.toString(),
        };
      }
      return item;
    });

    setTimeout(() => {
      this.refreshGrid.emit();
      this.changeDetector.markForCheck();
    },         100);
  }

  private updateGridDataWithLockStatus(auditPackageContext: AuditPackageContext): void {
    this.isAuditPackageLocked = !auditPackageContext?.isEditable;
    this.gridData = this.gridData.map(item => {
      return {
        ...item,
        isAuditPackageLocked: this.isAuditPackageLocked,
      };
    });

    if (this.isAuditPackageLocked) {
      const userName = `${auditPackageContext.firstName} ${auditPackageContext.lastName}`;
      this.messagingService.showToast(`This enrollee is currently being edited by another user (${userName}).`, SeverityType.ERROR);
    }
    this.refreshGrid.emit();
    this.changeDetector.markForCheck();
  }

  private loadAuditPackagesCount(enrollee: number): void {
    this.sink.add(
      this.auditPackageService
        .getAuditPackagesCount(enrollee)
        .subscribe(count => {
          this.totalAuditPackagesCount = count;
        })
    );
  }

  getAuditPackages(): any[] {
    return this.auditPackages;
  }

  getPageEntriesForAuditPackage(auditPackageId: number): any[] {
    return this.pageEntriesMap.get(auditPackageId) || [];
  }

  getActiveAuditPackageId(): number | null {
    if (this.auditPackages && this.auditPackages.length > 0) {
      return this.auditPackages[0].auditPackageID;
    }
    return null;
    }

}
